import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import useMergeState from "../../../shared/hooks/useMergeState";
import { api } from "../../../shared/hooks/api";
import { AccountingAccount } from "../../../data/entities";
import { error, success } from "../../../utils/toast";
import { CreateAccountingAccountTitleBar } from "../../../configs/accountingAccount";
import BackButton from "../../../shared/components/BackButton";
import Title from "./Title";
import CancelModal from "../../../shared/components/CancelModal";
import { WarningUnsavedChanges } from "../../../configs/warningProductModal";
import FormContainer from "./Content/FormContainer";
import { AccountingAccountWrapper } from "./Styled";

const initialData: Partial<AccountingAccount> = {
    code: "",
    name: "",
    type: "DU_NO",
    english_translation: "",
    note: "",
    status: "ACTIVE",
    parent: null,
};

const CreateAccountingAccount = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [validated, setValidated] = useState(false);

    // Check if we're cloning from location state
    const cloneData = location.state?.cloneData;
    const initialFormData = cloneData
        ? {
              ...cloneData,
              code: "", // Reset code for new account
              name: `${cloneData.name} (Copy)`, // Add copy suffix
              status: cloneData.status === "ACTIVE",
              parent: cloneData.parent,
          }
        : initialData;

    const [accountData, setAccountData] = useMergeState(initialFormData);
    const [{ isCreating }, createAccount] = api.post("/accounting/accounts/");
    const [isModified, setIsModified] = useState(false);
    const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
    const [navigationPath, setNavigationPath] = useState("");
    const [parentAccounts, setParentAccounts] = useState<AccountingAccount[]>(
        []
    );

    // Fetch parent accounts from the main list
    const [{ data: parentAccountsData }] = api.get("/accounting/accounts/", {
        page_size: 1000, // Get all accounts to filter parents
    });

    useEffect(() => {
        if (parentAccountsData?.results) {
            // Allow any account to be selected as parent (backend guards circular relationships)
            setParentAccounts(parentAccountsData.results);
        }
    }, [parentAccountsData]);

    // Mark as modified when accountData changes
    useEffect(() => {
        if (
            accountData.code ||
            accountData.name ||
            accountData.english_translation ||
            accountData.note
        ) {
            setIsModified(true);
        }
    }, [accountData]);

    // Add beforeunload event listener for browser navigation
    useEffect(() => {
        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            if (isModified) {
                e.preventDefault();
                e.returnValue = "";
                return "";
            }
        };

        window.addEventListener("beforeunload", handleBeforeUnload);
        return () => {
            window.removeEventListener("beforeunload", handleBeforeUnload);
        };
    }, [isModified]);

    const confirmNavigation = useCallback(
        (path: string) => {
            if (isModified) {
                setNavigationPath(path);
                setShowUnsavedDialog(true);
                return false;
            }

            navigate(path);
            return true;
        },
        [isModified, navigate]
    );

    const handleBackClick = () => {
        confirmNavigation("/accounting-accounts/");
    };

    const handleSaveAndNavigate = async () => {
        setShowUnsavedDialog(false);
        await handleSubmit();
    };

    const handleDontSaveAndNavigate = () => {
        setShowUnsavedDialog(false);
        navigate(navigationPath);
    };

    const handleContinueEditing = () => {
        setShowUnsavedDialog(false);
    };

    const handleSubmit = async () => {
        const requestBody = {
            code: accountData.code,
            name: accountData.name,
            english_translation: accountData.english_translation,
            note: accountData.note,
            status: accountData.status ? "ACTIVE" : "INACTIVE",
            parent: accountData.parent,
            type: accountData.type,
        };

        await createAccount(requestBody)
            .then((data: any) => {
                success("Thành Công", "Tạo mới tài khoản kế toán thành công");
                setIsModified(false);
                return true;
            })
            .catch((err: any) => {
                error("Lỗi tạo tài khoản kế toán", err);
                return false;
            });
    };

    const handleChangeAccountData = (updates: any) => {
        setIsModified(true);
        setAccountData(updates);
    };

    const title = cloneData
        ? "Sao chép tài khoản kế toán"
        : CreateAccountingAccountTitleBar.title;

    return (
        <AccountingAccountWrapper>
            <BackButton label="Trở về" onClick={handleBackClick} />
            <Title
                title={title}
                setValidated={setValidated}
                submitForm={handleSubmit}
                isLoading={isCreating}
            />
            <FormContainer
                validated={validated}
                accountData={accountData}
                mergeAccount={handleChangeAccountData}
                parentAccounts={parentAccounts}
                isCreate
            />

            <CancelModal
                open={showUnsavedDialog}
                handleClose={handleDontSaveAndNavigate}
                handleConfirm={handleSaveAndNavigate}
                handleCancel={handleContinueEditing}
                title={WarningUnsavedChanges.title}
                contentText={WarningUnsavedChanges.context}
                closeButtonText={WarningUnsavedChanges.closeButtonText}
                confirmButtonText={WarningUnsavedChanges.confirmButtonText}
                cancelButtonText={WarningUnsavedChanges.cancelButtonText}
                showCancelButton={true}
            />
        </AccountingAccountWrapper>
    );
};

export default CreateAccountingAccount;
